import { useContext } from 'react'

import {
  Button,
  Form,
  ModalContext,
  SidebarModal,
} from '@campaignhub/suit-theme'

import { digObject, formatDate } from '@campaignhub/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import useCredits, { useCreditForm } from '@hooks/useCredits'

const MODAL_KEY = 'GenerateCreditsCsvModal'

type ModalCallbacks = {
  closeModal: () => void,
  generateCreditsCSV: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const GenerateCreditsCsvModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, generateCreditsCSV } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { credit: initCredit } = modalPayload

  const {
    callbacks: { downloadCreditsCSV: downloadFn },
  } = useCredits(initCredit)

  const {
    entityState,
    entityState: {
      endDate,
      startDate,
    },
    errors,
    handlers,
    saveEnabled,
  } = useCreditForm({ status: modalPayload.filterStatusKey }, { validateOn: 'change' })

  // To be used with handleCallbackAction
  const generateCreditsCsvPayload = {
    callbacks: {
      action: downloadFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Credit Csv Created',
  }

  return (
    <SidebarModal callbacks={callbacks} modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Generate" titleSecondLine="Credit CSV" />

      <SidebarModal.Content>
        <Form>
          <Form.Field errorMessage={errors.startDate} label="* Start Date">
            <input
              name="startDate"
              type="date"
              value={formatDate(startDate, 'ISO8601')}
              {...handlers}
            />
          </Form.Field>

          <Form.Field label="End Date" marginTop="large">
            <input
              name="endDate"
              type="date"
              value={formatDate(endDate, 'ISO8601')}
              {...handlers}
            />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          disabled={!saveEnabled}
          buttonStyle="primaryCreate"
          onClick={() => generateCreditsCSV(generateCreditsCsvPayload)}
          size="large"
        >
          Generate Credit
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <GenerateCreditsCsvModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
